import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Trash2, Sparkles, Mail } from "lucide-react";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [tz, setTz] = useState(user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [rem, setRem] = useState({ d7: false, d3: true, d1: true, ...(user?.reminder_prefs || {}) });
  const [delOpen, setDelOpen] = useState(false);

  const save = async () => {
    try {
      const res = await api.patch("/account", { name, timezone: tz, reminder_prefs: rem });
      setUser(res.data);
      toast.success("Settings saved");
    } catch (e) { toast.error("Could not save settings"); }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "syllabify-data.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export data"); }
  };

  const testReminder = async () => {
    try {
      await api.post("/reminders/test");
      toast.success("Test reminder sent! Check your inbox.");
    } catch (e) {
      const d = e?.response?.data?.detail || "Could not send test reminder.";
      toast.error(d);
    }
  };

  const deleteAccount = async () => {    try {
      await api.delete("/account");
      toast.success("Account deleted");
      setUser(null);
      window.location.href = "/";
    } catch (e) { toast.error("Could not delete account"); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Settings</h1>

      {!user?.paid_status && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-medium text-blue-900">You're on the free plan</p>
            <p className="text-sm text-blue-800">Unlock unlimited courses & all features for a one-time $4.99.</p>
          </div>
          <Button onClick={() => navigate("/upgrade")} data-testid="settings-upgrade-btn" className="bg-blue-600 hover:bg-blue-700 rounded-lg shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Unlock</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Profile</h2>
        <div>
          <label className="text-xs text-slate-500">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Email</label>
          <Input value={user?.email || ""} disabled className="mt-1 bg-slate-50" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Time zone</label>
          <Input value={tz} onChange={(e) => setTz(e.target.value)} className="mt-1" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Reminder preferences</h2>
        <p className="text-xs text-slate-400">Get an email nudge before each deadline. Choose when.</p>
        {[["d7", "7 days before"], ["d3", "3 days before"], ["d1", "1 day before"]].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="text-sm text-slate-700">{label}</span>
            <Switch checked={rem[k]} onCheckedChange={(v) => setRem({ ...rem, [k]: v })} data-testid={`reminder-${k}`} />
          </div>
        ))}
        <Button variant="outline" onClick={testReminder} data-testid="test-reminder-btn" className="rounded-lg">
          <Mail className="h-4 w-4 mr-1" /> Send me a test reminder
        </Button>
      </div>

      <Button onClick={save} data-testid="settings-save-btn" className="bg-slate-900 hover:bg-slate-800 rounded-lg">Save changes</Button>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Your data</h2>
        <Button variant="outline" onClick={exportData} data-testid="export-data-btn" className="rounded-lg"><Download className="h-4 w-4 mr-1" /> Export my data</Button>
      </div>

      <div className="rounded-xl border border-red-200 bg-red-50/40 p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-red-500">Danger zone</h2>
        <p className="text-sm text-slate-600 mt-2 mb-4">Deleting your account permanently removes all your courses, syllabi, events, and grades.</p>
        <Button onClick={() => setDelOpen(true)} data-testid="delete-account-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-lg"><Trash2 className="h-4 w-4 mr-1" /> Delete account</Button>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-account-modal">
          <DialogHeader><DialogTitle>Delete your account?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This cannot be undone. All your data will be permanently deleted.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button onClick={deleteAccount} data-testid="confirm-delete-account" className="bg-red-600 hover:bg-red-700 text-white">Delete forever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
